import React, { useCallback, useEffect, useState } from 'react';

import {
  BackgroundCheckFact,
  BackgroundCheckState,
  LivenessState,
  MyVerification,
  completeLiveness,
  getMyVerification,
  refreshBackgroundCheck,
  startLiveness,
  submitBackgroundCheck,
} from '../api/teacher-verification';

/**
 * The teacher's two identity steps after the application: live selfie
 * (TCH-04-FR-05) and background check (TCH-03-FR-08).
 *
 * Provider decision (CTO, 12 Oct 2026): AWS Rekognition Face Liveness for the
 * selfie, Truora for LatAm background checks.
 *
 * Steps, not gates. Whether either one blocks publishing is decided by the
 * T03 eligibility derivation on the server; nothing on this screen enables or
 * disables anything else.
 */

const LIVENESS_LABEL: Record<LivenessState, string> = {
  NOT_STARTED: 'Pendiente',
  PENDING: 'En proceso',
  PASSED: 'Verificada',
  FAILED: 'No superada',
  EXPIRED: 'Sesión vencida',
};

const BACKGROUND_LABEL: Record<BackgroundCheckState, string> = {
  NOT_STARTED: 'Pendiente',
  SUBMITTED: 'Enviada',
  UNDER_REVIEW: 'En revisión',
  NEEDS_RESUBMIT: 'Necesita corrección',
  CLEARED: 'Aprobada',
  REJECTED: 'Requiere atención',
  EXPIRED: 'Vencida',
};

/**
 * Only the reasons the server lets a teacher read reach this map; each says
 * what to do next. Anything about findings or safety never arrives here
 * (AD-A07-10).
 */
const REASON_TEXT: Record<string, string> = {
  IDENTIFIER_NOT_VERIFIABLE:
    'No pudimos verificar ese número de identificación. Revísalo y vuelve a enviarlo.',
  PROVIDER_UNAVAILABLE:
    'El servicio de verificación no respondió. Vuelve a enviar para reintentarlo; tu autorización se conserva.',
  JURISDICTION_UNCONFIGURED:
    'Para tu país todavía no tenemos verificación automática. Nuestro equipo la revisará manualmente y te escribirá si necesita algún documento.',
  MANUAL_OFFICIAL_RECORD:
    'En tu país la verificación se hace con un registro oficial revisado por nuestro equipo. Te escribiremos si necesitamos algún documento.',
  MANUAL_RESUBMIT: 'Nuestro equipo necesita que vuelvas a enviar la consulta.',
};

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('es-MX') : '—');

/** When the teacher may (re)submit — mirrors the server's newAttemptAllowed. */
const canSubmitBackground = (fact: BackgroundCheckFact) =>
  fact.state === 'NOT_STARTED' ||
  fact.state === 'NEEDS_RESUBMIT' ||
  (fact.state === 'EXPIRED' && !fact.renewalInProgress) ||
  (fact.state === 'CLEARED' && fact.renewalStage !== 'CURRENT' && !fact.renewalInProgress) ||
  (fact.state === 'SUBMITTED' && fact.reasonCode === 'PROVIDER_UNAVAILABLE');

const VerificationSteps: React.FC = () => {
  const [verification, setVerification] = useState<MyVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [nationalId, setNationalId] = useState('');
  const [consent, setConsent] = useState(false);

  const load = useCallback(async () => {
    try {
      setVerification(await getMyVerification());
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar tu verificación.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo completar la acción.');
    } finally {
      setBusy(false);
    }
  };

  if (!verification) {
    return error ? <p style={{ color: '#c92a2a' }}>{error}</p> : null;
  }

  const { liveness, backgroundCheck } = verification;
  const reason = backgroundCheck.reasonCode && REASON_TEXT[backgroundCheck.reasonCode];
  const renewalNotice =
    backgroundCheck.state === 'CLEARED' &&
    ['NOTICE_FIRST', 'NOTICE_SECOND', 'RENEWAL_DUE'].includes(backgroundCheck.renewalStage);

  return (
    <section aria-labelledby="verification-heading">
      <h2 id="verification-heading" style={{ marginTop: 32 }}>
        Verificación de identidad
      </h2>
      {error && (
        <div role="alert" style={alertStyle}>
          {error}
        </div>
      )}

      <div style={cardStyle}>
        <div style={rowStyle}>
          <strong>Prueba de vida (selfie en vivo)</strong>
          <span data-testid="liveness-state">{LIVENESS_LABEL[liveness.state]}</span>
        </div>
        <p style={mutedStyle}>
          Confirmamos que eres tú, en persona, frente a la cámara. Solo guardamos el resultado de la
          verificación, nunca tu imagen.
        </p>
        {liveness.state !== 'PASSED' && !sessionId && (
          <button
            style={buttonStyle(busy)}
            disabled={busy}
            onClick={() =>
              run(async () => {
                const started = await startLiveness();
                if ('sessionId' in started) setSessionId(started.sessionId);
              })
            }
          >
            Iniciar prueba de vida
          </button>
        )}
        {sessionId && liveness.state !== 'PASSED' && (
          <>
            {/* The camera component (Amplify FaceLivenessDetector streaming to
                Rekognition with Identity Pool credentials) is not wired yet.
                The session is real and bound to this teacher; only the capture
                is missing. */}
            <p style={noteStyle}>
              La captura con cámara se está habilitando. Tu sesión quedó registrada; cuando la
              cámara esté disponible podrás completarla desde aquí.
            </p>
            <button
              style={buttonStyle(busy)}
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const { liveness: fact } = await completeLiveness(sessionId);
                  if (fact.state !== 'PENDING') setSessionId(null);
                })
              }
            >
              Comprobar resultado
            </button>
          </>
        )}
        {(liveness.state === 'FAILED' || liveness.state === 'EXPIRED') && !sessionId && (
          <p style={mutedStyle}>
            Puedes intentarlo de nuevo. Busca buena luz y mira de frente a la cámara.
          </p>
        )}
      </div>

      <div style={cardStyle}>
        <div style={rowStyle}>
          <strong>Antecedentes</strong>
          <span data-testid="background-state">{BACKGROUND_LABEL[backgroundCheck.state]}</span>
        </div>
        <p style={mutedStyle}>
          Consultamos antecedentes de cada profesor porque las familias confían menores de edad a
          quien enseña en IQMaxx. La consulta vale 12 meses y te avisaremos antes de que venza.
        </p>
        {backgroundCheck.state === 'CLEARED' && (
          <p style={mutedStyle}>Vigente hasta el {fmtDate(backgroundCheck.expiresAt)}.</p>
        )}
        {renewalNotice && (
          <p style={noteStyle}>
            Tus antecedentes vencen el {fmtDate(backgroundCheck.expiresAt)}. Renuévalos antes de esa
            fecha.
          </p>
        )}
        {backgroundCheck.renewalInProgress && (
          <p style={mutedStyle}>Tu renovación está en proceso.</p>
        )}
        {backgroundCheck.state === 'REJECTED' && (
          <p style={noteStyle}>
            Tu verificación de antecedentes requiere atención de nuestro equipo de confianza.
            Escríbenos a soporte.
          </p>
        )}
        {reason && <p style={noteStyle}>{reason}</p>}
        {backgroundCheck.state === 'SUBMITTED' && !canSubmitBackground(backgroundCheck) && (
          <button
            style={buttonStyle(busy)}
            disabled={busy}
            onClick={() =>
              run(async () => {
                await refreshBackgroundCheck();
              })
            }
          >
            Consultar estado
          </button>
        )}
        {canSubmitBackground(backgroundCheck) && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await submitBackgroundCheck({ nationalId: nationalId.trim(), consent });
                // Not kept in the page once sent: the number is only for this request.
                setNationalId('');
                setConsent(false);
              });
            }}
          >
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={labelStyle}>
                Número de identificación nacional (CURP, cédula, RUT o DNI)
              </span>
              <input
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span style={{ fontSize: 14 }}>
                Autorizo a IQMaxx y a su proveedor de verificación a consultar mis antecedentes con
                este número. No guardamos el número; solo el resultado de la consulta.
              </span>
            </label>
            <button
              type="submit"
              style={buttonStyle(busy || !consent || nationalId.trim().length < 5)}
              disabled={busy || !consent || nationalId.trim().length < 5}
            >
              Autorizar y enviar
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e9ecef',
  borderRadius: 6,
  padding: 12,
  marginBottom: 12,
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};
const mutedStyle: React.CSSProperties = { color: '#666', fontSize: 14 };
const noteStyle: React.CSSProperties = {
  background: '#fff4e6',
  border: '1px solid #ffd8a8',
  borderRadius: 6,
  color: '#5c3c00',
  padding: 10,
  fontSize: 14,
};
const alertStyle: React.CSSProperties = {
  background: '#ffe3e3',
  color: '#c92a2a',
  padding: 12,
  borderRadius: 6,
  marginBottom: 12,
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 8,
  border: '1px solid #ced4da',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};
const buttonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '10px 20px',
  border: 'none',
  borderRadius: 6,
  background: disabled ? '#a0c4e8' : '#1971c2',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default VerificationSteps;
